import Phaser from 'phaser';
import { signOut } from '@/systems/AuthService';
import { getActiveStudent, setActiveStudent } from '@/systems/ActiveStudent';
import { setDevModeEnabled } from '@/systems/DevMode';
import { saveSystem } from '@/systems/SaveSystem';
import { createStudent, deleteStudent, listStudents, type Student } from '@/systems/StudentService';
import { addBackdrop } from '@/ui/background';
import { Button } from '@/ui/components/Button';
import { Layout, Palette, Typography } from '@/ui/theme';
import { fadeInScene, fadeToScene } from '@/utils/transitions';

/**
 * ProfileSelectScene — entre o login do pai e o gameplay.
 *
 * Lista os alunos do responsável, permite criar um novo (modal) e
 * iniciar a sessão de jogo escolhendo qual aluno está jogando. Se o
 * pai não tem nenhum aluno ainda, força a criação antes de seguir.
 */
export class ProfileSelectScene extends Phaser.Scene {
  private students: Student[] = [];
  private statusText?: Phaser.GameObjects.Text;
  private listContainer?: Phaser.GameObjects.Container;

  constructor() {
    super({ key: 'ProfileSelectScene' });
  }

  create(): void {
    addBackdrop(this);
    fadeInScene(this);
    const { width, height } = this.scale;

    this.add
      .text(width / 2, 80, 'Quem vai jogar?', {
        fontFamily: Typography.fontFamily,
        fontSize: '48px',
        color: Palette.textPrimaryCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, 130, 'Escolha um aluno para começar a sessão', {
        fontFamily: Typography.fontFamily,
        fontSize: '20px',
        color: Palette.textMutedCss,
      })
      .setOrigin(0.5);

    this.statusText = this.add
      .text(width / 2, height - 120, 'Carregando alunos...', {
        fontFamily: Typography.fontFamily,
        fontSize: '16px',
        color: Palette.textMutedCss,
      })
      .setOrigin(0.5);

    this.listContainer = this.add.container(0, 0);

    new Button(this, width / 2 - 160, height - 60, '+ Novo aluno', {
      width: 280,
      height: 56,
      fontSize: 18,
      onClick: () => this.handleAddStudent(),
    });

    new Button(this, width / 2 + 160, height - 60, 'Sair da conta', {
      width: 280,
      height: 56,
      fontSize: 18,
      fillColor: 0x2a2a4a,
      hoverColor: Palette.danger,
      onClick: () => this.handleSignOut(),
    });

    // Voltar pra MenuScene só faz sentido quando já existe aluno ativo
    // (cenário "Gerenciar alunos" vindo do menu). No fluxo inicial
    // pós-login o aluno é null e essa rota levaria a um menu sem perfil.
    if (getActiveStudent()) {
      new Button(this, 96, 56, '← Voltar', {
        width: 140,
        height: 44,
        fontSize: 16,
        fillColor: 0x2a2a4a,
        hoverColor: Palette.primary,
        onClick: () => fadeToScene(this, 'MenuScene'),
      });
    }

    void this.refreshList();
  }

  private async refreshList(): Promise<void> {
    this.setStatus('Carregando alunos...', 'muted');
    const result = await listStudents();
    if (!result.ok) {
      this.setStatus(`Erro: ${result.error}`, 'error');
      return;
    }
    this.students = result.value;
    this.renderList();
    if (this.students.length === 0) {
      this.setStatus('Nenhum aluno ainda — crie o primeiro pra começar!', 'muted');
    } else {
      this.setStatus('', 'muted');
    }
  }

  private renderList(): void {
    if (!this.listContainer) return;
    this.listContainer.removeAll(true);
    const { width } = this.scale;

    const cols = Math.min(3, Math.max(1, this.students.length));
    const cardW = 280;
    const cardH = 140;
    const gap = 24;
    const totalW = cols * cardW + (cols - 1) * gap;
    const startX = width / 2 - totalW / 2;
    const startY = 200;

    this.students.forEach((student, idx) => {
      const col = idx % cols;
      const row = Math.floor(idx / cols);
      const x = startX + col * (cardW + gap) + cardW / 2;
      const y = startY + row * (cardH + gap) + cardH / 2;
      this.renderStudentCard(student, x, y, cardW, cardH);
    });
  }

  private renderStudentCard(student: Student, x: number, y: number, w: number, h: number): void {
    if (!this.listContainer) return;
    const baseColor = Palette.primary;

    const bg = this.add.graphics();
    bg.fillStyle(baseColor, 1);
    bg.fillRoundedRect(x - w / 2, y - h / 2, w, h, Layout.cornerRadius);

    const name = this.add
      .text(x, y - 12, student.displayName, {
        fontFamily: Typography.fontFamily,
        fontSize: '28px',
        color: Palette.textPrimaryCss,
        fontStyle: 'bold',
        wordWrap: { width: w - 32 },
        align: 'center',
      })
      .setOrigin(0.5);

    const cta = this.add
      .text(x, y + 32, '▶ Jogar', {
        fontFamily: Typography.fontFamily,
        fontSize: '18px',
        color: Palette.textMutedCss,
      })
      .setOrigin(0.5);

    const removeBtn = this.add
      .text(x + w / 2 - 16, y - h / 2 + 14, '✕', {
        fontFamily: Typography.fontFamily,
        fontSize: '16px',
        color: Palette.textDimCss,
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    removeBtn.on(Phaser.Input.Events.POINTER_OVER, () => removeBtn.setColor(Palette.dangerCss));
    removeBtn.on(Phaser.Input.Events.POINTER_OUT, () => removeBtn.setColor(Palette.textDimCss));
    removeBtn.on(Phaser.Input.Events.POINTER_UP, (ev: Phaser.Input.Pointer) => {
      ev.event?.stopPropagation?.();
      void this.handleDeleteStudent(student);
    });

    const hit = this.add.rectangle(x, y, w, h, 0xffffff, 0).setInteractive({ useHandCursor: true });
    hit.on(Phaser.Input.Events.POINTER_OVER, () => {
      bg.clear();
      bg.lineStyle(3, Palette.textPrimary, 1);
      bg.strokeRoundedRect(x - w / 2, y - h / 2, w, h, Layout.cornerRadius);
      bg.fillStyle(baseColor, 1);
      bg.fillRoundedRect(x - w / 2, y - h / 2, w, h, Layout.cornerRadius);
    });
    hit.on(Phaser.Input.Events.POINTER_OUT, () => {
      bg.clear();
      bg.fillStyle(baseColor, 1);
      bg.fillRoundedRect(x - w / 2, y - h / 2, w, h, Layout.cornerRadius);
    });
    hit.on(Phaser.Input.Events.POINTER_UP, () => this.handleSelectStudent(student));

    this.listContainer.add([bg, name, cta, removeBtn, hit]);
  }

  private async handleSelectStudent(student: Student): Promise<void> {
    setActiveStudent(student);
    this.setStatus('Carregando progresso...', 'muted');
    try {
      await saveSystem.bindToStudent(student.id);
    } catch (err) {
      console.error('[ProfileSelectScene] bindToStudent falhou', err);
      this.setStatus('Erro ao carregar progresso. Tente novamente.', 'error');
      return;
    }
    fadeToScene(this, 'MenuScene');
  }

  private async handleAddStudent(): Promise<void> {
    const name = window.prompt('Nome ou apelido do aluno:');
    if (name === null) return;
    this.setStatus('Cadastrando aluno...', 'muted');
    const result = await createStudent(name);
    if (!result.ok) {
      this.setStatus(`Erro: ${result.error}`, 'error');
      return;
    }
    this.students.push(result.value);
    this.renderList();
    this.setStatus(`Aluno "${result.value.displayName}" criado.`, 'success');
  }

  private async handleDeleteStudent(student: Student): Promise<void> {
    const confirm = window.confirm(
      `Excluir o aluno "${student.displayName}" e todo o progresso dele? Essa ação não pode ser desfeita.`,
    );
    if (!confirm) return;
    this.setStatus('Excluindo aluno...', 'muted');
    const result = await deleteStudent(student.id);
    if (!result.ok) {
      this.setStatus(`Erro: ${result.error}`, 'error');
      return;
    }
    this.students = this.students.filter((s) => s.id !== student.id);
    this.renderList();
    this.setStatus(`Aluno removido.`, 'success');
  }

  private async handleSignOut(): Promise<void> {
    setActiveStudent(null);
    setDevModeEnabled(false);
    const result = await signOut();
    if (!result.ok) {
      this.setStatus(`Erro ao sair: ${result.error}`, 'error');
      return;
    }
    fadeToScene(this, 'AuthScene');
  }

  private setStatus(message: string, kind: 'muted' | 'success' | 'error'): void {
    if (!this.statusText) return;
    const colorMap = {
      muted: Palette.textMutedCss,
      success: Palette.successCss,
      error: Palette.dangerCss,
    } as const;
    this.statusText.setColor(colorMap[kind]).setText(message);
  }
}
